import { useEffect, useMemo, useState } from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppTextField } from "../../../components/form";
import { AppFormField, AppFormGrid } from "../../../components/layout";
import { GRID_HALF, GRID_THIRD } from "../../../lib/ui/formLayout";
import {
  buildScheduleCron,
  parseScheduleCron,
  parseTimeInput,
  SCHEDULE_DAY_OF_WEEK_OPTIONS,
  timeInputValue,
  type ScheduleConfig,
  type SchedulePreset,
} from "../utils/prepScheduleCron";

type Props = {
  value?: string;
  onChange: (cron: string) => void;
  /** Hide "No schedule" option (required schedules such as insight jobs). */
  hideNone?: boolean;
  label?: string;
  helperText?: string;
};

type PresetOption = {
  value: SchedulePreset;
  label: string;
};

type DayOfWeekOption = {
  value: string;
  label: string;
};

const PRESET_ORDER: SchedulePreset[] = [
  "none",
  "everyMinute",
  "every15Minutes",
  "every30Minutes",
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "yearly",
  "custom",
];

export default function PrepScheduleCronField({
  value,
  onChange,
  hideNone = false,
  label,
  helperText,
}: Props) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<ScheduleConfig>(() => {
    const parsed = parseScheduleCron(value);
    if (hideNone && parsed.preset === "none") {
      return { ...parsed, preset: "daily", hour: 8, minute: 0 };
    }
    return parsed;
  });

  useEffect(() => {
    const parsed = parseScheduleCron(value);
    if (hideNone && parsed.preset === "none") {
      setConfig({ ...parsed, preset: "daily", hour: 8, minute: 0 });
      return;
    }
    setConfig(parsed);
  }, [value, hideNone]);

  const cronPreview = useMemo(() => buildScheduleCron(config), [config]);

  const presetOptions = useMemo<PresetOption[]>(
    () =>
      PRESET_ORDER.filter((preset) => !(hideNone && preset === "none")).map((preset) => ({
        value: preset,
        label: t(`prep.drawer.schedulePreset.${preset}`),
      })),
    [t, hideNone]
  );

  const dayOfWeekOptions = useMemo<DayOfWeekOption[]>(
    () =>
      SCHEDULE_DAY_OF_WEEK_OPTIONS.map(({ value: dow }) => ({
        value: dow,
        label: t(`prep.drawer.scheduleDow.${dow}`),
      })),
    [t]
  );

  const selectedPreset =
    presetOptions.find((option) => option.value === config.preset) ?? presetOptions[0];

  const selectedDayOfWeek =
    dayOfWeekOptions.find((option) => option.value === (config.dayOfWeek ?? "MON")) ??
    dayOfWeekOptions[1];

  const update = (patch: Partial<ScheduleConfig>) => {
    const next = { ...config, ...patch };
    setConfig(next);
    onChange(buildScheduleCron(next));
  };

  const showTimeFields =
    config.preset === "daily" ||
    config.preset === "weekly" ||
    config.preset === "monthly" ||
    config.preset === "yearly";

  return (
    <Stack spacing={1.5}>
      <AppAutocomplete<PresetOption>
        label={label ?? t("prep.drawer.scheduleCron")}
        helperText={helperText ?? t("prep.drawer.scheduleCronHelp")}
        options={presetOptions}
        value={selectedPreset}
        onChange={(_, option) => {
          if (option) update({ preset: option.value });
        }}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        disableClearable
        fullWidth
      />

      {config.preset === "hourly" && (
        <AppFormGrid spacing="compact">
          <AppFormField size={GRID_HALF}>
            <AppTextField
              type="number"
              label={t("prep.drawer.scheduleAtMinute")}
              value={config.minute ?? 0}
              onChange={(e) => update({ minute: Number(e.target.value) })}
              inputProps={{ min: 0, max: 59 }}
              fullWidth
            />
          </AppFormField>
        </AppFormGrid>
      )}

      {showTimeFields && (
        <AppFormGrid spacing="compact">
          <AppFormField size={config.preset === "monthly" ? GRID_THIRD : GRID_HALF}>
            <AppTextField
              type="time"
              label={t("prep.drawer.scheduleTime")}
              value={timeInputValue(config.hour ?? 0, config.minute ?? 0)}
              onChange={(e) => {
                const { hour, minute } = parseTimeInput(e.target.value);
                update({ hour, minute });
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </AppFormField>
          {config.preset === "weekly" && (
            <AppFormField size={GRID_HALF}>
              <AppAutocomplete<DayOfWeekOption>
                label={t("prep.drawer.scheduleDayOfWeek")}
                options={dayOfWeekOptions}
                value={selectedDayOfWeek}
                onChange={(_, option) => {
                  if (option) update({ dayOfWeek: option.value });
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                disableClearable
                fullWidth
              />
            </AppFormField>
          )}
          {config.preset === "monthly" && (
            <AppFormField size={GRID_THIRD}>
              <AppTextField
                type="number"
                label={t("prep.drawer.scheduleDayOfMonth")}
                value={config.dayOfMonth ?? 1}
                onChange={(e) => update({ dayOfMonth: Number(e.target.value) })}
                inputProps={{ min: 1, max: 28 }}
                helperText={t("prep.drawer.scheduleDayOfMonthHelp")}
                fullWidth
              />
            </AppFormField>
          )}
        </AppFormGrid>
      )}

      {config.preset === "custom" && (
        <AppTextField
          label={t("prep.drawer.scheduleCustomCron")}
          value={config.customCron ?? ""}
          onChange={(e) => update({ customCron: e.target.value })}
          placeholder="0 0 0 * * *"
          helperText={t("prep.drawer.scheduleCustomCronHelp")}
          fullWidth
        />
      )}

      {config.preset !== "none" && cronPreview && (
        <Typography variant="caption" color="text.secondary" component="div">
          {t("prep.drawer.scheduleCronPreview", { cron: cronPreview })}
        </Typography>
      )}
    </Stack>
  );
}
